import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Check, X, Loader2, Plug, AlertTriangle } from "lucide-react";

/**
 * Turns the `connected_at` timestamp into a friendly "connected N days ago"
 * label. Purely informational — the tone/action state of the banner is now
 * driven by `status.healthy` (a real Google refresh probe on the backend)
 * rather than age, because published OAuth apps keep refresh tokens
 * indefinitely and an age-based red chip creates false alarms.
 */
function tokenAgeLabel(connectedAt) {
  if (!connectedAt) return null;
  const then = new Date(connectedAt);
  if (Number.isNaN(then.getTime())) return null;
  const ms = Date.now() - then.getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor(ms / (1000 * 60 * 60));

  let label;
  if (days >= 2) label = `${days} days ago`;
  else if (days === 1) label = "yesterday";
  else if (hours >= 1) label = `${hours} hour${hours === 1 ? "" : "s"} ago`;
  else label = "just now";
  return { label, iso: then.toISOString() };
}


export default function GoogleConnectBanner() {
  const [status, setStatus] = useState(null); // {configured, connected, email, connected_at}
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/google/status");
      setStatus(data);
    } catch {
      setStatus({ configured: false, connected: false });
    } finally {
      setLoading(false);
    }
  }, []);

  // One-time mount: surface the OAuth-callback redirect result (?ga4_connected / ?ga4_error).
  // Kept separate from loadStatus so the state-loading effect has no side branches.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("ga4_connected");
    const err = params.get("ga4_error");
    if (!connected && !err) return;
    const url = new URL(window.location.href);
    if (connected === "1") {
      toast.success("Google Analytics connected.");
      url.searchParams.delete("ga4_connected");
    }
    if (err) {
      toast.error(`Google Analytics connect failed: ${err}`);
      url.searchParams.delete("ga4_error");
    }
    window.history.replaceState({}, "", url.toString());
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const connect = useCallback(async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/auth/google/start");
      // full-page redirect — easiest UX, the callback bounces back here
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Google OAuth flow");
      setConnecting(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    if (!confirm("Disconnect Google Analytics? GA4 cards will stop loading until you reconnect.")) return;
    try {
      await api.post("/auth/google/disconnect");
      toast.success("Disconnected from Google Analytics.");
      loadStatus();
    } catch {
      toast.error("Failed to disconnect");
    }
  }, [loadStatus]);

  if (loading || !status?.configured) return null;

  const age = tokenAgeLabel(status?.connected_at);

  if (status.connected) {
    // Tone is now driven by the real Google refresh-probe result (healthy),
    // NOT by token age. Age is purely informational because a published
    // OAuth consent screen means refresh tokens stay valid indefinitely.
    // `healthy === null` = probe hasn't run yet or can't (missing token).
    const isBroken = status.healthy === false;
    const tone = isBroken ? "danger" : "ok";

    const toneClasses = {
      ok: "bg-[#4fd1c5]/10 border-[#4fd1c5]/30 text-[#234e52]",
      danger: "bg-[#fed7d7] border-[#f56565]/50 text-[#742a2a]",
    };
    const chipClasses = {
      ok: "bg-[#4fd1c5]/20 text-[#2c7a7b]",
      danger: "bg-[#f56565]/20 text-[#742a2a]",
    };

    return (
      <div
        className={`rounded-xl border ${toneClasses[tone]}`}
        data-testid="ga4-status-banner"
        data-healthy={String(status.healthy)}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-2.5">
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className={`h-6 w-6 rounded-md inline-flex items-center justify-center ${chipClasses[tone]}`}>
              {isBroken
                ? <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.6} />
                : <Check className="h-3.5 w-3.5" strokeWidth={2.6} />}
            </span>
            <span className="font-mono-tight">
              Google Analytics connected{status.email ? ` as ${status.email}` : ""}.
            </span>
            {age && (
              <button
                type="button"
                onClick={() => setShowHint((s) => !s)}
                title={age.iso}
                data-testid="ga4-token-age-chip"
                className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${chipClasses[tone]} cursor-help`}
              >
                Connected {age.label}
                {isBroken && " · reconnect required"}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={disconnect}
            data-testid="ga4-disconnect-btn"
            className="text-[10px] uppercase tracking-widest font-bold opacity-60 hover:opacity-100 hover:text-[#742a2a] flex items-center gap-1"
          >
            <X className="h-3 w-3" /> Disconnect
          </button>
        </div>
        {(isBroken || showHint) && (
          <div
            data-testid="ga4-token-age-hint"
            className="text-[11px] leading-[1.55] px-4 pb-2.5 pt-0 opacity-90"
          >
            {isBroken ? (
              <>
                Google refused to refresh this token{status.health_error ? ` (${status.health_error})` : ""}. This usually means the token was revoked or the OAuth consent screen was reverted to Testing mode. Click <b>Disconnect</b> and reconnect below to fix it.
              </>
            ) : (
              <>
                Token verified against Google — no action needed. Refresh tokens stay valid indefinitely while your OAuth consent screen is in <b>In production</b> mode.{" "}
                <a
                  href="https://console.cloud.google.com/apis/credentials/consent"
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-semibold"
                >
                  Open Google Cloud Console →
                </a>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex items-start sm:items-center justify-between gap-4 p-4 rounded-2xl bg-white border border-[#e2e8f0] shadow-[0_4px_12px_-2px_rgba(45,55,72,0.06)]"
      data-testid="ga4-connect-banner"
    >
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 rounded-xl bg-[#4fd1c5]/10 text-[#2c7a7b] inline-flex items-center justify-center shrink-0">
          <Plug className="h-4 w-4" strokeWidth={2.4} />
        </span>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold">Optional integration</div>
          <h3 className="font-display text-base mt-0.5 text-[#1a202c]">Connect Google Analytics</h3>
          <p className="text-xs text-[#4a5568] mt-1 max-w-md">
            One-time sign-in. Pull sessions, users, engaged sessions, conversions and top organic landing pages for any GA4 property you have access to.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={connect}
        disabled={connecting}
        data-testid="ga4-connect-btn"
        className="btn-primary inline-flex items-center gap-2 shrink-0 disabled:opacity-60"
      >
        {connecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />}
        Connect
      </button>
    </div>
  );
}
